import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogIn, LogOut as LogOutIcon, DoorOpen, Ban, Activity } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const CircleDonut = ({ segments, size = 130, thickness = 14, center }) => {
  const total = Math.max(segments.reduce((s, x) => s + x.value, 0), 1);
  let acc = 0;
  const stops = segments.map((s) => {
    const from = (acc / total) * 360;
    acc += s.value;
    const to = (acc / total) * 360;
    return `${s.color} ${from}deg ${to}deg`;
  }).join(", ");
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(${stops || "#F1F5F9 0deg 360deg"})` }} />
      <div className="absolute rounded-full bg-white flex items-center justify-center" style={{ inset: thickness }}>
        <div className="text-center">
          <p className="font-display font-extrabold text-2xl text-slate-900 leading-none">{center.value}</p>
          <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{center.label}</p>
        </div>
      </div>
    </div>
  );
};

const Card = ({ title, icon: Icon, tone, children, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-5">
    <div className="flex items-center gap-2 mb-4">
      <div className={`w-8 h-8 rounded-lg ${tone} flex items-center justify-center`}><Icon size={14} className="text-white" /></div>
      <h4 className="font-display font-bold text-slate-900 text-sm">{title}</h4>
    </div>
    {children}
  </div>
);

const Legend = ({ items }) => (
  <div className="flex-1 min-w-0 space-y-1.5">
    {items.map((it) => (
      <div key={it.label} className="flex items-center gap-2 text-[12px] whitespace-nowrap">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: it.color }} />
        <span className="text-slate-500 truncate">{it.label}</span>
        <span className="ml-auto font-mono font-bold text-slate-800">{it.value}</span>
      </div>
    ))}
  </div>
);

export default function OpsStrip({ pid, refreshKey }) {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get(`/pms/properties/${pid}/ops-stats`).then(({ data }) => setS(data))
      .catch((e) => toast.error(errMsg(e)));
  }, [pid, refreshKey]);

  if (!s) return <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 h-[186px]">
    {[0, 1, 2, 3].map(i => <div key={i} className="bg-slate-50 border border-slate-100 rounded-2xl animate-pulse" />)}
  </div>;

  const timeAgo = (iso) => {
    const diff = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (diff < 1) return "just now";
    if (diff < 60) return `${diff}m ago`;
    if (diff < 60 * 24) return `${Math.round(diff / 60)}h ago`;
    return `${Math.round(diff / 60 / 24)}d ago`;
  };

  const actionBadge = (a) => {
    if (a.startsWith("reservation.checked_in")) return { label: "Checked in", color: "bg-emerald-50 text-emerald-600" };
    if (a.startsWith("reservation.checked_out")) return { label: "Checked out", color: "bg-slate-100 text-slate-500" };
    if (a.startsWith("reservation.moved")) return { label: "Unit moved", color: "bg-sky-50 text-sky-600" };
    if (a.startsWith("reservation.cancelled")) return { label: "Cancelled", color: "bg-red-50 text-red-500" };
    if (a.startsWith("reservation.no_show")) return { label: "No-show", color: "bg-orange-50 text-orange-500" };
    if (a.startsWith("reservation.created")) return { label: "New booking", color: "bg-emerald-50 text-emerald-600" };
    if (a.startsWith("payment.recorded")) return { label: "Payment", color: "bg-emerald-50 text-emerald-600" };
    if (a.startsWith("calendar.blocked")) return { label: "Unit block", color: "bg-slate-100 text-slate-600" };
    if (a.startsWith("calendar.unblocked")) return { label: "Unblocked", color: "bg-slate-100 text-slate-500" };
    if (a.startsWith("voucher.")) return { label: "Voucher", color: "bg-amber-50 text-amber-600" };
    return { label: a.split(".").pop().replace(/_/g, " "), color: "bg-slate-100 text-slate-500" };
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      <Card testid="ops-arrivals" title="Arrivals today" icon={LogIn} tone="bg-emerald-500">
        <div className="flex items-center gap-4">
          <CircleDonut segments={[
            { value: s.arrivals.pending, color: "#F59E0B" },
            { value: s.arrivals.total - s.arrivals.pending, color: "#10B981" },
            { value: Math.max(1, 0), color: "#F1F5F9" }].filter((x, i) => i < 2 || s.arrivals.total === 0)}
            center={{ value: s.arrivals.total, label: "Total" }} />
          <Legend items={[
            { label: "Pending check-in", value: s.arrivals.pending, color: "#F59E0B" },
            { label: "Checked in", value: s.arrivals.total - s.arrivals.pending, color: "#10B981" },
          ]} />
        </div>
      </Card>

      <Card testid="ops-departures" title="Departures today" icon={LogOutIcon} tone="bg-amber-500">
        <div className="flex items-center gap-4">
          <CircleDonut segments={[
            { value: s.departures.pending, color: "#F59E0B" },
            { value: s.departures.checked_out, color: "#94A3B8" }].filter(x => x.value > 0)}
            center={{ value: s.departures.total, label: "Total" }} />
          <Legend items={[
            { label: "Pending", value: s.departures.pending, color: "#F59E0B" },
            { label: "Checked out", value: s.departures.checked_out, color: "#94A3B8" },
          ]} />
        </div>
      </Card>

      <Card testid="ops-rooms" title="Unit status now" icon={DoorOpen} tone="bg-[#4F46E5]">
        <div className="flex items-center gap-4">
          <CircleDonut segments={[
            { value: s.room_status.sold, color: "#4F46E5" },
            { value: s.room_status.blocked, color: "#1E293B" },
            { value: s.room_status.vacant, color: "#E2E8F0" }].filter(x => x.value > 0)}
            center={{ value: s.room_status.total, label: "Units" }} />
          <Legend items={[
            { label: "Sold", value: s.room_status.sold, color: "#4F46E5" },
            { label: "Blocked", value: s.room_status.blocked, color: "#1E293B" },
            { label: "Vacant", value: s.room_status.vacant, color: "#94A3B8" },
          ]} />
        </div>
      </Card>

      <Card testid="ops-hk" title="Housekeeping" icon={Activity} tone="bg-sky-500">
        <div className="flex items-center gap-4">
          <CircleDonut segments={[
            { value: s.housekeeping.clean, color: "#10B981" },
            { value: s.housekeeping.dirty, color: "#F59E0B" },
            { value: s.housekeeping.block, color: "#1E293B" }].filter(x => x.value > 0)}
            center={{ value: s.housekeeping.total, label: "Units" }} />
          <Legend items={[
            { label: "Clean", value: s.housekeeping.clean, color: "#10B981" },
            { label: "Dirty", value: s.housekeeping.dirty, color: "#F59E0B" },
            { label: "Block", value: s.housekeeping.block, color: "#1E293B" },
          ]} />
        </div>
      </Card>
      </div>

      <div data-testid="activity-feed" className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display font-bold text-slate-900 text-sm">Activity Feed</h4>
          <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Last {s.activity.length} events</span>
        </div>
        {s.activity.length === 0 ? (
          <p className="text-xs text-slate-400 py-4 text-center">No activity yet.</p>
        ) : (
          <div className="divide-y divide-slate-50">
            {s.activity.map((a, i) => {
              const badge = actionBadge(a.action);
              return (
                <div key={i} className="flex items-start justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-700 truncate" title={a.detail}>{a.detail}</p>
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">{timeAgo(a.at)}</p>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${badge.color}`}>{badge.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
